<template>
  <Dialog
    modal
    :visible="shouldClone"
    :dismissable-mask="true"
    :closable="false"
    :show-header="false"
    @show="onDialogShow"
    @hide="onDialogHide"
  >
    <form class="p-1" @submit.prevent="saveClone">
      <p class="text-xl mb-4">Save Clone As</p>
      <InputText
        ref="textInput"
        v-model="cloneName"
        :placeholder="`${type} Name`"
        class="text-blue-500"
        autofocus
        required
        @keydown.esc="closeDialog"
      />
    </form>
    <template #footer>
      <Button label="Cancel" @click="closeDialog" />
      <Button
        label="Save"
        class="p-button-info"
        :disabled="isSubmitting"
        @click="saveClone"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { useRoute } from "vue-router";

defineProps<{
  type: "Sizing" | "Scenario";
  shouldClone: boolean;
}>();

const emit = defineEmits<{
  (e: "update:shouldClone", value: boolean): void;
  (e: "save-clone", title: string): void;
}>();

const textInput = ref();
const isSubmitting = ref(false);
const cloneName = ref("");

const route = useRoute();
watch(
  () => route.params.scenarioId,
  () => {
    cloneName.value = "";
    isSubmitting.value = false;
  }
);

async function onDialogShow() {
  // Wait for dialog to be fully visible before focusing
  await nextTick();

  setTimeout(() => {
    if (textInput.value?.$el) {
      // Force blur on any active element first
      const activeElement = document.activeElement as HTMLElement;
      activeElement?.blur();

      // Now force focus on our input
      textInput.value.$el.focus();
      textInput.value.$el.select();

      // Additional fallback - try direct DOM focus
      const inputElement = textInput.value.$el.querySelector("input");
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    }
  }, 50); // Small delay to ensure DOM is ready
}

function onDialogHide() {
  cloneName.value = "";
  isSubmitting.value = false;
}

function closeDialog() {
  emit("update:shouldClone", false);
}

function saveClone() {
  if (!cloneName.value || isSubmitting.value) return;
  isSubmitting.value = true;
  emit("save-clone", cloneName.value);
}
</script>
